import ExcelJS from "exceljs";
import type { VideoRow } from "../../api";

/**
 * Итог месяца по сотрудникам (25.10.2026, Жулдыз): сколько роликов вышло и
 * сколько они набрали — для ежемесячных премий. Ролик в счёт месяца, в
 * котором вышел; просмотры — последние известные на день выгрузки.
 */

export interface MonthPerson {
  id: string;
  name: string;
  projects: string[];
  published: number;
  /** null — ни у одного ролика ещё нет цифр */
  views: number | null;
  rows: VideoRow[];
}

const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

/** «2026-10» → «Октябрь 2026». */
export const monthLabel = (month: string) => `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

/** Первый и последний день месяца, «2026-10» → ["2026-10-01", "2026-10-31"]. */
export function monthRange(month: string): [string, string] {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return [`${month}-01`, `${month}-${String(last).padStart(2, "0")}`];
}

/** Текущий и пять прошлых месяцев по Алматы, новые сверху. */
export function recentMonths(now = Date.now(), count = 6): string[] {
  const local = new Date(now + 5 * 3600e3);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth() - i, 1));
    return d.toISOString().slice(0, 7);
  });
}

export function monthPeople(rows: VideoRow[]): MonthPerson[] {
  const map = new Map<string, MonthPerson>();
  for (const r of rows) {
    if (r.status !== "published") continue;
    const p = map.get(r.editor.id) ?? { id: r.editor.id, name: r.editor.name, projects: [], published: 0, views: null, rows: [] };
    p.published += 1;
    if (r.stats?.views != null) p.views = (p.views ?? 0) + r.stats.views;
    if (r.project && !p.projects.includes(r.project.name)) p.projects.push(r.project.name);
    p.rows.push(r);
    map.set(r.editor.id, p);
  }
  return [...map.values()].sort((a, b) => b.published - a.published || (b.views ?? 0) - (a.views ?? 0));
}

export async function exportMonthXlsx(month: string, people: MonthPerson[], scope: string): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();
  const asOf = new Date().toLocaleDateString("ru-RU", { timeZone: "Asia/Almaty" });

  const total = wb.addWorksheet("Итог");
  total.addRow([`${monthLabel(month)} · ${scope}`]).font = { bold: true, size: 13 };
  total.addRow([`Вышедшие ролики месяца; просмотры — на ${asOf}`]);
  total.addRow([]);
  total.addRow(["Сотрудник", "Направления", "Роликов вышло", "Просмотры", "Среднее на ролик"]).font = { bold: true };
  for (const p of people) {
    total.addRow([p.name, p.projects.join(", "), p.published, p.views ?? "—", p.views != null ? Math.round(p.views / p.published) : "—"]);
  }
  const all = people.reduce((n, p) => n + p.published, 0);
  const views = people.some((p) => p.views != null) ? people.reduce((n, p) => n + (p.views ?? 0), 0) : null;
  total.addRow(["Итого", "", all, views ?? "—", views != null && all ? Math.round(views / all) : "—"]).font = { bold: true };
  total.columns.forEach((c, i) => { c.width = [28, 26, 15, 14, 18][i]; });

  const list = wb.addWorksheet("Ролики");
  list.addRow(["Сотрудник", "Ролик", "Направление", "Аккаунт", "Вышел", "Просмотры", "Ссылка"]).font = { bold: true };
  for (const p of people) {
    for (const r of p.rows) {
      list.addRow([
        p.name, r.title, r.project?.name ?? "", r.account?.name ?? "",
        r.publishedAt ? new Date(r.publishedAt).toLocaleDateString("ru-RU", { timeZone: "Asia/Almaty" }) : "",
        r.stats?.views ?? "—",
        r.link ? { text: r.link, hyperlink: r.link } : "",
      ]);
    }
  }
  list.columns.forEach((c, i) => { c.width = [24, 48, 16, 20, 12, 12, 50][i]; });

  const buf = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `Ролики ${monthLabel(month)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
